import type { DeclineReason, TaskAnswer, TaskDoc, TaskRow } from "@/lib/api/tasks"
import type { TaskKind, TaskResult } from "./task-types"

// What she can log, as data. The tools (the calendar, the estimate) are
// always on the desk; these are the chips under "After the call". A chip
// posts, runs something outside the engine, or OPENS ONE ROW under itself
// (a day, a reason, a number) — never deeper. Dark = do, white = log,
// red = close. Green is the calendar's alone.

export type Tone = "do" | "log" | "close"
export type Opens = "days" | "reasons" | "number" | "stop" | "notToday"
export type VisitMode = "ask" | "rebook"

/** The writes a decision can make outside the task engine. */
export interface TaskApi {
  setVisitOutcome: (appointmentId: number, outcome: "attended" | "no_show") => Promise<void>
  savePhone: (estimateId: number, phone: string) => Promise<void>
  stopTexts: (customerId: number) => Promise<void>
  /** Puts a call back on the list for an estimate with nothing open. */
  wake: (estimateId: number) => Promise<void>
  /** Back to In Progress with a call in N business days. */
  followUpCall: (estimateId: number, inDays: number) => Promise<void>
  /** The customer said no, from somewhere that isn't a task answer. */
  decline: (estimateId: number, reason: DeclineReason, note: string | null) => Promise<void>
  openEdit: (estimateId: number) => void
  /** Straight to In Progress: the texts start today. The board's "start follow-up". */
  startFollowUp: (estimateId: number) => Promise<void>
  /** "Not today" — tomorrow morning, nothing logged. */
  skip: () => Promise<TaskDoc>
  /** "Pick a day" — the texts wait until then. */
  snooze: (until: string, note: string | null) => Promise<void>
}

export interface DecisionCtx {
  row: TaskRow
  teamId: number
  note: string
  api: TaskApi
  switchToCall?: () => void
}

/** What a decision resolves to:
 *  `post` → POST /tasks/:id/answer (the note and the restart flag ride along);
 *           `after` runs once the answer landed, `startedTexts` marks the result
 *  `done` → recorded through another endpoint; just report it
 *  `mode` → the visit card moves to its next step (the calendar follows)
 *  null   → nothing happened (aborted, or handed off elsewhere) */
export type Resolution =
  | { post: TaskAnswer; after?: (ctx: DecisionCtx) => Promise<void>; startedTexts?: boolean }
  | { done: TaskResult }
  | { mode: VisitMode }
  | null

export interface Decision {
  id: string
  label: string
  tone: Tone
  when?: (row: TaskRow) => boolean
  disabledWhy?: (ctx: DecisionCtx) => string | null
  post?: TaskAnswer
  run?: (ctx: DecisionCtx) => Promise<Resolution>
  opens?: Opens
  /** For `opens: "reasons"` and `"stop"`: the list, and what a reason resolves to. */
  reasons?: { list: DeclineReason[]; resolve: (r: DeclineReason, ctx: DecisionCtx) => Promise<Resolution> }
  /** For `opens: "days"`: the answer a chosen day becomes. */
  callback?: (iso: string) => TaskAnswer
}

export interface DecisionGroup {
  label?: (row: TaskRow) => string
  chips: Decision[]
}

export interface Decisions {
  heading: (row: TaskRow) => string
  groups: DecisionGroup[]
  /** One quiet line under the chips, when the calendar is the other half of the answer. */
  hint?: (row: TaskRow) => string
}

const first = (r: TaskRow) => r.customerName.split(" ")[0] || r.customerName

export const REACHED_REASONS: DeclineReason[] = ["price", "another_shop", "insurance_total_loss", "not_ready", "no_longer_needed", "other"]
export const CLOSE_REASONS: DeclineReason[] = ["unreachable", "price", "another_shop", "insurance_total_loss", "not_ready", "no_longer_needed", "other"]
const REVIEW_REASONS: DeclineReason[] = ["no_longer_needed", "insurance_total_loss", "another_shop", "other"]

const closeWith = async (r: DeclineReason): Promise<Resolution> => ({ post: { kind: "closed", declineReason: r } })

const CALL: Decisions = {
  heading: () => "After the call",
  groups: [
    {
      label: (r) => `Reached ${first(r)}`,
      chips: [
        { id: "thinking", label: "Still thinking", tone: "log", post: { kind: "reached", then: "thinking" } },
        { id: "changes", label: "Wants changes", tone: "log", post: { kind: "reached", then: "needs_changes" } },
        { id: "callback", label: "Call back on…", tone: "log", opens: "days", callback: (iso) => ({ kind: "reached", then: "callback", callbackAt: iso }) },
        {
          id: "declined",
          label: "Not interested",
          tone: "close",
          opens: "reasons",
          reasons: {
            list: REACHED_REASONS,
            resolve: async (r, ctx) => ({
              post: { kind: "reached", then: "declined", declineReason: r, ...(r === "other" ? { declineNote: ctx.note } : {}) },
            }),
          },
        },
      ],
    },
    {
      label: (r) => `Didn't reach ${first(r)}`,
      chips: [
        { id: "no_answer", label: "No answer", tone: "log", post: { kind: "no_answer" } },
        { id: "bad_number", label: "Wrong number", tone: "log", opens: "number" },
      ],
    },
  ],
}

export const DECISIONS: Record<Exclude<TaskKind, "visit">, Decisions> = {
  call: CALL,
  wrapup: {
    heading: () => "Close it, or try again?",
    groups: [
      {
        chips: [
          { id: "again", label: "Call again on…", tone: "log", opens: "days", callback: (iso) => ({ kind: "keep_trying", callbackAt: iso }) },
          { id: "close", label: "Close it", tone: "close", opens: "reasons", reasons: { list: CLOSE_REASONS, resolve: closeWith } },
        ],
      },
    ],
    hint: (r) => `Or book ${first(r)} on the right.`,
  },
  review: {
    heading: () => "Good to go?",
    groups: [
      {
        chips: [
          { id: "confirm", label: "Confirm it", tone: "do", post: { kind: "confirmed" } },
          {
            id: "confirm_start",
            label: "Confirm and start follow-up",
            tone: "log",
            // Confirm first (the review closes, the first call appears), THEN
            // In Progress — the other order would let the review answer pull
            // the estimate back to Confirmed.
            run: async () => ({
              post: { kind: "confirmed" },
              after: (ctx) => ctx.api.startFollowUp(ctx.row.estimateId),
              startedTexts: true,
            }),
          },
          {
            id: "edit",
            label: "Open to edit",
            tone: "log",
            run: async (ctx) => {
              ctx.api.openEdit(ctx.row.estimateId)
              return null
            },
          },
          { id: "notreal", label: "Not a real job", tone: "close", opens: "reasons", reasons: { list: REVIEW_REASONS, resolve: closeWith } },
        ],
      },
    ],
  },
  reply: {
    heading: () => "Or:",
    groups: [
      {
        chips: [
          {
            id: "called",
            label: "Call them instead",
            tone: "log",
            run: async (ctx) => {
              ctx.switchToCall?.()
              return null
            },
          },
          {
            id: "stop",
            label: "Stop the texts",
            tone: "close",
            opens: "stop",
            when: (r) => r.customerId != null && r.dripState !== "stopped",
            reasons: {
              list: REACHED_REASONS,
              resolve: async (r, ctx) => {
                await ctx.api.decline(ctx.row.estimateId, r, r === "other" ? ctx.note || null : null)
                return { done: { kind: "declined", reason: r } }
              },
            },
          },
        ],
      },
    ],
    hint: (r) => `Or book ${first(r)} on the right.`,
  },
  fix: {
    heading: () => "Or",
    groups: [{ chips: [{ id: "cant", label: "Can't get one", tone: "close", post: { kind: "closed", declineReason: "unreachable" } }] }],
  },
}

/** The visit card walks three steps; the calendar on the right follows. */
export function visitDecisions(mode: VisitMode): Decisions {
  switch (mode) {
    case "ask":
      return {
        heading: (r) => `Did ${first(r)} come in?`,
        groups: [
          {
            chips: [
              {
                id: "attended",
                label: "Yes, came in",
                tone: "do",
                run: async (ctx) => {
                  await ctx.api.setVisitOutcome(ctx.row.appointmentId!, "attended")
                  return { done: { kind: "visit", outcome: "attended" } }
                },
              },
              { id: "no_show", label: "Didn't show", tone: "log", run: async () => ({ mode: "rebook" }) },
            ],
          },
        ],
      }
    case "rebook":
      return {
        heading: (r) => `Book ${first(r)} again?`,
        groups: [
          {
            chips: [
              {
                id: "back",
                label: "Back to follow-up",
                tone: "log",
                run: async (ctx) => {
                  await ctx.api.setVisitOutcome(ctx.row.appointmentId!, "no_show")
                  await ctx.api.wake(ctx.row.estimateId)
                  return { done: { kind: "visit", outcome: "no_show" } }
                },
              },
            ],
          },
        ],
        hint: () => "Pick a new time on the right.",
      }
  }
}

/** Kinds where a note travels with the pick. A review has nothing to log;
 *  a visit's answers go to the calendar, not the engine. */
export const hasNoteField = (kind: TaskKind) => kind !== "review" && kind !== "visit"

export function decisionsFor(kind: TaskKind, mode: VisitMode): Decisions {
  return kind === "visit" ? visitDecisions(mode) : DECISIONS[kind]
}

/** The restart switch, only when the sequence has run out. Not a decision. */
export const canRestart = (row: TaskRow) => row.dripState === "finished"

/** THE one place restartDrip is decided. It never rides on a terminal answer:
 *  re-enrolling a customer who just booked or just said no is the bug. */
export function withRestart(a: TaskAnswer, restart: boolean): TaskAnswer {
  if (!restart || a.kind !== "reached") return a
  if (a.then === "booked" || a.then === "declined") return a
  return { ...a, restartDrip: true }
}

export const CALLBACK_PRESETS = [
  { label: "Tomorrow", days: 1 },
  { label: "In 2 days", days: 2 },
  { label: "Next week", days: 5 },
] as const

/** N business days out, at 09:00 — "tomorrow" on a Friday is Monday. */
export function callbackDate(days: number, from = new Date()): Date {
  const d = new Date(from)
  let left = days
  while (left > 0) {
    d.setDate(d.getDate() + 1)
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1
  }
  d.setHours(9, 0, 0, 0)
  return d
}

export function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
